/* eslint-disable no-loop-func */
/* eslint-disable no-lonely-if */
import { v4 as uuidv4 } from 'uuid';

import { isInThePast } from 'util';

export const convertToRecurringData = ({
  data,
  increment, // in seconds
}) => {
  if (!data && (!data.startStr || !data.endStr)) return [];
  const { startStr, endStr } = data;

  const startDate = new Date(startStr);
  const endDate = new Date(endStr);
  const time = [];

  // Helper function to create a new date with rounded down to the nearest minute
  function roundToNearestMinute(date) {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
      )
    );
  }

  let current = roundToNearestMinute(new Date(startDate));

  while (current < endDate) {
    let nextInterval = new Date(current.getTime() + increment * 1000); // Add increment seconds

    // Adjust nextInterval if it goes beyond the endDate
    if (nextInterval > endDate) {
      nextInterval = endDate;
    }

    // Round down nextInterval to the nearest minute
    nextInterval = roundToNearestMinute(nextInterval);

    time.push({
      start: current.toISOString(),
      end: nextInterval.toISOString(),
      uuid: uuidv4(), // Assuming uuidv4 is defined/imported
    });

    // Prepare for the next iteration
    current = new Date(nextInterval.getTime() + 1000); // Move to the next second in UTC

    // Ensure the loop ends if current is equal to or goes beyond endDate
    if (current >= endDate) {
      break;
    }

    // Round current to the nearest minute to ensure :00 seconds
    current = roundToNearestMinute(current);
  }

  return time;
};

export function cleanEvents(events) {
  // events is coming from redux, so objects inside it are immutable.
  // We need to make a copy
  const eventsCopy = events.map((event) => ({
    ...event,
  }));

  // Sort events by date, then by start time
  const sortedEvents = eventsCopy.sort((a, b) => {
    const dateComparison = new Date(a.eventDate) - new Date(b.eventDate);
    if (dateComparison !== 0) {
      return dateComparison;
    }
    return new Date(a.start) - new Date(b.start);
  });

  const seen = new Set();
  const uniqueArray = sortedEvents.filter((obj) => {
    const key = `${obj.start}|${obj.end}`;
    if (!seen.has(key)) {
      seen.add(key);
      return true;
    }
    return false;
  });

  return uniqueArray;
}

// Group and create ranges for availability, per groomer
export function groupAvailabilityPerGroomer(events) {
  if (!events || !Array.isArray(events)) {
    return [];
  }

  // If events is coming from redux, so objects inside it are immutable.
  // We need to make a copy
  const eventsCopy = events.map((event) => ({
    ...event,
  }));

  const grouped = eventsCopy.reduce((acc, obj) => {
    const key = obj.resourceId;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  return Object.values(grouped)
    .map((el) => createRanges(el))
    .flat();
}

// Merge availability slots
// This will convert:
// { startTime: 09:00, endTime: 09:30}, { startTime: 09:30, endTime: 10:00 }
// into
// { startTime: 09:00, endTime: 10:00}
export function createRanges(events) {
  if (events.length === 0) return [];

  // If events is coming from redux, so objects inside it are immutable.
  // We need to make a copy
  const eventsCopy = events.map((event) => ({
    ...event,
  }));

  const mergedEvents = [];

  eventsCopy.forEach((event) => {
    if (mergedEvents.length === 0) {
      mergedEvents.push({
        ...event,
        backgroundColor: isInThePast(event.start) ? '#fff8f4' : '#FFEDE3',
        borderColor: isInThePast(event.start) ? '#fff8f4' : '#FFEDE3',
      });
      return;
    }

    // Get the last event in the mergedEvents array
    const lastEvent = mergedEvents[mergedEvents.length - 1];
    if (
      lastEvent.eventDate === event.eventDate &&
      new Date(lastEvent.end) >= new Date(event.start)
    ) {
      // Merge the events by updating the end of the last event in the mergedEvents array
      lastEvent.end = event.end;
    } else {
      // If they cannot be merged, add the current event to the mergedEvents array
      mergedEvents.push({
        ...event,
        backgroundColor: isInThePast(event.start) ? '#fff8f4' : '#FFEDE3',
        borderColor: isInThePast(event.start) ? '#fff8f4' : '#FFEDE3',
      });
    }
  });

  return mergedEvents;
}

// Turn an awway of big ranges into slots with 30 min increments, with a 15 riminder if needed.
// This will convert:
// [{dateStart: '2024-03-27T05:00:00.000Z', dateEnd: '2024-03-27T05:30:00.000Z'},
// {dateStart: '2024-03-27T06:00:00.000Z', dateEnd: '2024-03-27T07:15:00.000Z'}]
// into
// {dateStart: '2024-03-27T05:00:00.000Z', dateEnd: '2024-03-27T05:30:00.000Z'}
// {dateStart: '2024-03-27T06:00:00.000Z', dateEnd:  '2024-03-27T06:30:00.000Z'}
// {dateStart: '2024-03-27T06:30:00.000Z', dateEnd:  '2024-03-27T07:00:00.000Z'}
// {dateStart: '2024-03-27T07:00:00.000Z', dateEnd:  '2024-03-27T07:15:00.000Z'}

export function breakIntoIncrements(events) {
  if (!events && events.length === 0) return [];
  const increments = [];

  events.forEach((event) => {
    let currentDate = new Date(event.dateStart);
    const endDate = new Date(event.dateEnd);

    while (currentDate < endDate) {
      const nextDate = new Date(currentDate.getTime() + 30 * 60000); // Add 30 minutes
      if (nextDate > endDate) {
        // If nextDate is beyond the endDate, push the remainder
        increments.push({
          dateStart: currentDate.toISOString(),
          dateEnd: endDate.toISOString(),
        });
      } else {
        increments.push({
          dateStart: currentDate.toISOString(),
          dateEnd: nextDate.toISOString(),
        });
      }
      currentDate = nextDate;
    }
  });

  return removeDuplicates(increments);
}

// Removes duplicate ranges from an array like this
// [{dateStart: '2024-03-27T05:00:00.000Z', dateEnd: '2024-03-27T05:30:00.000Z'},
// {dateStart: '2024-03-27T05:00:00.000Z', dateEnd: '2024-03-27T05:30:00.000Z'}]
export function removeDuplicates(events) {
  const uniqueEventsSet = new Set();
  const uniqueEvents = [];

  events.forEach((event) => {
    const eventString = JSON.stringify(event);
    if (!uniqueEventsSet.has(eventString)) {
      uniqueEventsSet.add(eventString);
      uniqueEvents.push(event);
    }
  });

  return uniqueEvents;
}

export function formatTime(date) {
  // Manually format the time as HH:MM:SS in UTC
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

export function roundDownTime(timeStr) {
  // Split the time string into hours, minutes, and seconds
  const parts = timeStr.split(':');

  // Reconstruct the time string with seconds set to "00"
  const roundedTime = `${parts[0]}:${parts[1]}:00`;

  return roundedTime;
}

export function findMatchingRanges(availability, selectedRanges) {
  return selectedRanges.filter((bItem) =>
    availability.some(
      (aItem) => aItem.start === bItem.start && aItem.end === bItem.end
    )
  );
}

export function removeDeletedSlots(arrayA, arrayB) {
  const stringifiedB = new Set(arrayB.map((event) => JSON.stringify(event)));
  return arrayA.filter((eventA) => !stringifiedB.has(JSON.stringify(eventA)));
}

export function filterOutExistingSlots(
  selectedSlotsData,
  todaysRecurring,
  todaysSingle
) {
  const mergedRecurringSlots = createRanges(todaysRecurring);
  const filteredOutSingleSlots = filterOutSingleSlots(
    selectedSlotsData,
    todaysSingle
  );
  const removedSlotsOverlappingRecurring = filterOverlaps(
    filteredOutSingleSlots,
    mergedRecurringSlots
  );

  return removedSlotsOverlappingRecurring;
}

const filterOutSingleSlots = (selectedSlotsData, todaysSingle) =>
  selectedSlotsData.filter(
    (aElement) =>
      !todaysSingle.some(
        (bElement) =>
          aElement.start === bElement.start && aElement.end === bElement.end
      )
  );

function filterOverlaps(arrayA, arrayB) {
  // Filter out any objects in arrayA that overlap with any objects in arrayB
  return arrayA.filter(
    (a) =>
      !arrayB.some((b) => {
        // Check if 'a' starts during 'b'
        const startA = new Date(a.start);
        const endA = new Date(a.end);
        const startB = new Date(b.start);
        const endB = new Date(b.end);
        return (
          (startA >= startB && startA < endB) || (endA > startB && endA <= endB)
        );
      })
  );
}
